import React, { useCallback, useState } from 'react';
import { Building2, Clock, DollarSign, Download } from 'lucide-react';
import { useVoiceTenantCallAggregates } from './useVoiceCalls';
import { fetchVoiceCallAggregates } from './voice-calls-api';
import { buildVoiceTenantCsvLines, getVoiceCsvFilename } from './voice-csv-utils';
import { formatCost, formatDuration } from './voice-utils';
import { downloadCsv } from '../../lib/csv-utils';
import { formatPeriodLabel } from '../../lib/date-range-utils';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { DateRangePicker } from '../../components/ui/date-range-picker';
import { useToast } from '../../hooks/useToast';
import { SummaryMetric } from './SummaryMetric';
import { VoiceTenantsTable } from './VoiceTenantsTable';
import styles from './VoiceUsageTab.module.css';

export const VoiceUsageTab: React.FC = () => {
  const [selectedDays, setSelectedDays] = useState<number>(0);
  const [isExporting, setIsExporting] = useState(false);
  const { addToast } = useToast();

  const {
    data,
    summary,
    dateRange,
    dateRangeIsResolved,
    isLoading,
    isFetching,
    summaryIsLoading,
    summaryError,
    error,
    hasNextPage,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  } = useVoiceTenantCallAggregates(selectedDays);

  const activeTenants = summary?.activeTenantCount ?? 0;
  const totalDurationSecs = summary?.totalDurationSecs ?? 0;
  const totalCostCents = summary?.totalCostCents ?? 0;
  const showSummaryPlaceholders = summaryIsLoading && (summary === null || summary === undefined);
  const summaryFailed = summaryError !== null && summaryError !== undefined;

  const periodLabel = formatPeriodLabel(selectedDays);
  const { startDate: rangeStartDate, endDate: rangeEndDate } = dateRange;

  const handleDownloadCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      let cursor: string | undefined;
      let morePages = true;
      const csvLines: string[] = [];
      const filename = getVoiceCsvFilename(selectedDays);

      while (morePages) {
        const response = await fetchVoiceCallAggregates({
          cursor,
          limit: 100,
          startDate: rangeStartDate,
          endDate: rangeEndDate,
        });

        const pageCsvLines = buildVoiceTenantCsvLines(response.data);
        if (csvLines.length === 0) {
          csvLines.push(...pageCsvLines);
        } else {
          csvLines.push(...pageCsvLines.slice(1));
        }

        morePages = response.hasNextPage;
        cursor = response.cursor;
      }

      downloadCsv(filename, csvLines);
    } catch (error) {
      console.error('Failed to export Assist CSV', error);
      addToast({
        variant: 'destructive',
        title: 'Failed to export CSV',
        description:
          error instanceof Error
            ? error.message
            : 'Something went wrong while exporting Assist usage data.',
      });
    } finally {
      setIsExporting(false);
    }
  }, [addToast, selectedDays, rangeStartDate, rangeEndDate]);

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <DateRangePicker
          selectedDays={selectedDays}
          onSelectDays={setSelectedDays}
          isLoading={isFetching}
        />
        <Button
          variant="ghost"
          onClick={() => {
            void handleDownloadCsv();
          }}
          disabled={!dateRangeIsResolved || isLoading || isFetching || isExporting}>
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? 'Exporting...' : 'Download CSV'}
        </Button>
      </div>

      <div
        className={styles.summaryGrid}
        data-fetching={isFetching ? 'true' : 'false'}
        aria-busy={showSummaryPlaceholders}>
        <Card className={styles.summaryCard} data-fetching={isFetching ? 'true' : 'false'}>
          <div className={styles.summaryCardHeader}>
            <div>
              <p className={styles.summaryTitle}>Tenants with Calls ({periodLabel})</p>
              <p className={styles.summaryValue}>
                <SummaryMetric
                  showDash={summaryFailed}
                  isLoading={showSummaryPlaceholders}
                  skeletonClassName={styles.metricSkeletonNarrow}>
                  {activeTenants}
                </SummaryMetric>
              </p>
            </div>
            <div className={`${styles.summaryIcon} ${styles.summaryIconTenants}`}>
              <Building2 />
            </div>
          </div>
        </Card>
        <Card className={styles.summaryCard} data-fetching={isFetching ? 'true' : 'false'}>
          <div className={styles.summaryCardHeader}>
            <div>
              <p className={styles.summaryTitle}>Total Time ({periodLabel})</p>
              <p className={styles.summaryValue}>
                <SummaryMetric
                  showDash={summaryFailed}
                  isLoading={showSummaryPlaceholders}
                  skeletonClassName={styles.metricSkeletonMedium}>
                  {formatDuration(totalDurationSecs)}
                </SummaryMetric>
              </p>
            </div>
            <div className={`${styles.summaryIcon} ${styles.summaryIconDuration}`}>
              <Clock />
            </div>
          </div>
        </Card>
        <Card className={styles.summaryCard} data-fetching={isFetching ? 'true' : 'false'}>
          <div className={styles.summaryCardHeader}>
            <div>
              <p className={styles.summaryTitle}>Total Usage Cost ({periodLabel})</p>
              <p className={styles.summaryValue}>
                <SummaryMetric
                  showDash={summaryFailed}
                  isLoading={showSummaryPlaceholders}
                  skeletonClassName={styles.metricSkeletonWide}>
                  {formatCost(totalCostCents)}
                </SummaryMetric>
              </p>
            </div>
            <div className={`${styles.summaryIcon} ${styles.summaryIconCost}`}>
              <DollarSign />
            </div>
          </div>
        </Card>
      </div>

      <VoiceTenantsTable
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        hasNextPage={hasNextPage}
        currentPage={currentPage}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
      />
    </div>
  );
};
